import { get, type Writable, writable } from 'svelte/store'
import { AnalyticalParentType, AnalyticalType } from '$types/enums'
import { fetchUtils } from '$utils/fetchUtils'
import { tick } from 'svelte'
import type { Recommendation } from './types/recommendation'
import type { AnalyticalMetadata } from './types/analyticalMetadata'
import type { AnalyticalValue } from './types/analyticalValue'

const sessionValues = window.sessionStorage.getItem('analyticalValues')
export const values: Writable<AnalyticalValue[]> = writable(
	sessionValues ? JSON.parse(sessionValues) : []
)
export const parameterStore: Writable<{ parameters: Record<string, string> }> = writable({
	parameters: {}
})
export const recommendationParameters: Writable<Record<string, Record<string, string>>> = writable(
	{}
)
export const recommendationValues: Writable<Record<string, Writable<AnalyticalValue[]>>> = writable(
	{}
)
export const recommendations: Writable<Recommendation[]> = writable([])
export const selectedRecommendation = writable<string | null>()
export const hideOptionalValues: Writable<boolean> = writable(true)

export const analyticalMetadata: Writable<AnalyticalMetadata> = writable({
	parentType: AnalyticalParentType.LOT,
	parentId: null,
	analyticalId: null,
	analyticalType: AnalyticalType.ANALYSIS,
	hasOnlySingleExpertResult: false,
	technicalName: null
})

export const analyticNames: Writable<Record<string, string>> = writable({})
export const analyticsToolParams: Writable<Record<string, string>> = writable()

export const findValue = (key: string) => {
	const valuesArr = get(values)
	for (let i = 0; i < valuesArr.length; i++) {
		const value = valuesArr[i]
		if (value.key === key) {
			return value
		}
	}

	return null
}

export const getFormattedParameters = () => {
	const res = []
	const parameters = get(parameterStore).parameters
	for (const parametersKey in parameters) {
		res.push({
			name: parametersKey,
			value: parameters[parametersKey]
		})
	}
	return res
}

export function loadRecommendations() {
	const { analyticalId, analyticalType, parentType, technicalName } = get(analyticalMetadata)
	const baseUrl = window.location.href
	if (analyticalType === AnalyticalType.ANALYTIC) {
		// for analytics do nothing
		return
	}
	const url = new URL(`/api/${analyticalType?.toLowerCase()}/recommendations`, baseUrl)
	if (!parentType || parentType === AnalyticalParentType.NONE) {
		const parameters = getCurrentAnalyticalParameters()
		const payloadForTool = {
			parameters,
			technicalName,
			values: get(values)
		}
		/**
		 * recommendations enrich the page, they are not the page: a permission
		 * failure must not replace a working analysis with an error page.
		 *
		 * the catch is required, not decorative — `doHandle` rejects whatever the
		 * flag says, and `void` discards the value, not the rejection. without it
		 * suppressing the page error just converts it into an unhandled rejection.
		 * the empty-recommendations fallback is the defined state.
		 */
		void fetchUtils
			.post(url.toString(), payloadForTool, false)
			.then(setRecommendations)
			.catch(() => setRecommendations({ recommendations: [] }))
	} else {
		url.searchParams.append('parentType', parentType || 'LOT')
		url.searchParams.append('analyticalId', analyticalId || '')
		void fetchUtils
			.get(url.toString(), false)
			.then(setRecommendations)
			.catch(() => setRecommendations({ recommendations: [] }))
	}
}

const setRecommendations = async (data: { recommendations: Recommendation[] }) => {
	recommendations.set(data.recommendations)
	await tick()
	initializeRecommendationStores(data.recommendations)
}

const getCurrentAnalyticalParameters = () => {
	const paramsStore = get(parameterStore).parameters
	return Object.keys(paramsStore).map((parametersKey) => ({
		name: parametersKey,
		value: paramsStore[parametersKey]
	}))
}

const initializeRecommendationStores = (recs: Recommendation[]) => {
	const paramsStore = get(parameterStore).parameters
	const currentValues = get(recommendationValues)
	const currentParams = get(recommendationParameters)

	for (const rec of recs) {
		const key = rec.analytic

		// initialize values store per analytic if not existing
		if (!(key in currentValues)) {
			currentValues[key] = writable(rec.values)
		}

		if (!currentParams[key]) {
			currentParams[key] = {}
		}
		// always update all the values according to the parameterStore
		for (const [paramKey, paramValue] of Object.entries(rec.requiredParameters)) {
			if (paramsStore[paramKey]) {
				currentParams[key][paramKey] = paramsStore[paramKey]
			} else if (!currentParams[key][paramKey]) {
				currentParams[key][paramKey] = paramValue[0]
			}
		}
	}

	recommendationValues.set(currentValues)
	recommendationParameters.set(currentParams)
}
